import type { Ref } from 'preact';
import {
  ExternalLink,
  LogIn,
  LogOut,
  MessageSquareOff,
  MessageSquarePlus,
  Power,
  User,
} from 'lucide-preact';
import type { ReviewerCache } from '../identity';
import { truncate } from '../lib/format';
import { t } from '../i18n';

export interface FabButtonProps {
  fabRef: Ref<HTMLButtonElement>;
  menuRef: Ref<HTMLDivElement>;
  menuOpen: boolean;
  setMenuOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  launcherActive: boolean;
  hasPending: boolean;
  commentCount: number;
  loggedIn: boolean;
  demoMode: boolean;
  reviewer: ReviewerCache | null;
  projectSlug: string | null;
  /** Server-resolved prototype version the reviewer is currently looking at —
   *  driven by ?pinmerge_v= (or the project's default). NULL when the project
   *  has no versions configured; in that case no badge renders. */
  activeVersion: { id: string; slug: string; label: string } | null;
  onTogglePicking: () => void;
  onOpenDashboard: () => void;
  onOpenLogin: () => void;
  onLogout: () => void;
  onExitReview: () => void;
}

export function FabButton(props: FabButtonProps) {
  const {
    fabRef,
    menuRef,
    menuOpen,
    setMenuOpen,
    launcherActive,
    hasPending,
    commentCount,
    loggedIn,
    demoMode,
    reviewer,
    projectSlug,
    activeVersion,
    onTogglePicking,
    onOpenDashboard,
    onOpenLogin,
    onLogout,
    onExitReview,
  } = props;
  return (
    <>
      <button
        ref={fabRef}
        type="button"
        class={`fab ${launcherActive ? 'fab--active' : ''}`}
        onClick={() => setMenuOpen((v) => !v)}
        title={t('fab.open-menu')}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <CommentIcon size={22} />
        {commentCount > 0 && (
          <span class="fab__badge" aria-label={t('fab.comment-badge', { count: commentCount })}>
            {commentCount > 99 ? '99+' : commentCount}
          </span>
        )}
        {hasPending && <span class="fab__sync" title={t('fab.syncing')} aria-hidden="true" />}
      </button>

      {activeVersion && (
        <span
          class="fab__version-badge"
          title={t('fab.version-label', { label: activeVersion.label })}
          aria-label={t('fab.version-label', { label: activeVersion.label })}
        >
          {activeVersion.label}
        </span>
      )}

      {menuOpen && (
        <div ref={menuRef} class="fab-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            class={`fab-menu__item ${launcherActive ? 'fab-menu__item--primary' : ''}`}
            onClick={onTogglePicking}
          >
            {launcherActive ? <MessageSquareOff size={16} /> : <MessageSquarePlus size={16} />}
            <span>
              {launcherActive ? t('fab.toggle-pick-off') : t('fab.toggle-pick-on')}
            </span>
            <kbd class="fab-menu__kbd" title={t('fab.shortcut-hint')}>C</kbd>
          </button>

          {projectSlug && loggedIn && !demoMode && (
            <button type="button" role="menuitem" class="fab-menu__item" onClick={onOpenDashboard}>
              <ExternalLink size={16} />
              <span>{t('fab.open-dashboard')}</span>
            </button>
          )}

          {(loggedIn || (!demoMode && !loggedIn)) && <div class="fab-menu__sep" />}

          {loggedIn && reviewer && (
            <div class="fab-menu__label" title={reviewer.email}>
              <User size={16} />
              <span>{truncate(reviewer.email, 28)}</span>
            </div>
          )}

          {!loggedIn && !demoMode && (
            <button
              type="button"
              role="menuitem"
              class="fab-menu__item"
              onClick={() => {
                setMenuOpen(false);
                onOpenLogin();
              }}
            >
              <LogIn size={16} />
              <span>{t('fab.signin')}</span>
            </button>
          )}

          {loggedIn && !demoMode && (
            <button
              type="button"
              role="menuitem"
              class="fab-menu__item"
              onClick={() => {
                setMenuOpen(false);
                onLogout();
              }}
            >
              <LogOut size={16} />
              <span>{t('fab.signout')}</span>
            </button>
          )}

          <div class="fab-menu__sep" />

          <button
            type="button"
            role="menuitem"
            class="fab-menu__item fab-menu__item--danger"
            onClick={() => {
              setMenuOpen(false);
              onExitReview();
            }}
          >
            <Power size={16} />
            <span>{t('fab.exit-review')}</span>
          </button>
        </div>
      )}
    </>
  );
}

// Brand icon shown on the FAB. Inlined so the widget bundle doesn't need an
// extra HTTP fetch for an asset. Coloured via inline fill — the cream
// "post-it" face stays visible on the dark FAB; the black inner detail sits
// on top of it.
function CommentIcon({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" aria-hidden="true">
      <g clip-path="url(#pinmerge-comment-icon-clip)">
        <path
          d="M56.0027 69.8662C58.8674 74.1319 68.2619 83.1827 69.6637 84.1769L70.7345 71.0697C83.6984 72.1287 87.7326 62.6845 88.1291 57.83L89.6758 38.8973C90.8656 24.3338 80.4825 19.8203 74.9581 19.369C69.4337 18.9177 42.9761 16.0232 30.7629 15.7585C18.5497 15.4938 14.4618 24.6894 13.8074 32.6993L12.2607 51.632C11.2667 63.7993 22.3554 67.2396 26.5393 67.4592L56.0027 69.8662Z"
          fill="#F9F7E8"
        />
        <path
          d="M14.8042 32.7808C15.122 28.8908 16.2693 24.8092 18.7298 21.7533C21.1538 18.7426 24.9255 16.6327 30.7408 16.7586C42.9164 17.0225 69.2829 19.9092 74.8768 20.3662C77.4565 20.577 81.2021 21.7465 84.143 24.5633C87.0452 27.3431 89.2516 31.8088 88.6792 38.8159L87.1325 57.749C86.949 59.9936 85.9122 63.34 83.4177 65.9807C80.9611 68.5811 77.0157 70.5791 70.8161 70.0727L69.8194 69.9913L68.8272 82.1363C68.5843 81.9139 68.3159 81.6656 68.0257 81.3928C66.9128 80.347 65.5261 78.9979 64.0934 77.5462C61.2055 74.62 58.2055 71.3526 56.8328 69.3085L56.5647 68.9084L26.6059 66.461L26.5913 66.4608C24.6619 66.3594 21.0468 65.4953 18.0634 63.2293C15.1365 61.0062 12.7912 57.4233 13.2575 51.7138L14.8042 32.7808ZM11.2641 51.551C10.7367 58.0083 13.4387 62.2289 16.8534 64.8226C20.0015 67.2137 23.7319 68.2125 26.0437 68.4256L26.4869 68.4579L55.4509 70.8241C57.0298 73.0393 59.9618 76.2067 62.6697 78.9506C64.1187 80.4188 65.5232 81.7855 66.6555 82.8496C67.763 83.8903 68.6665 84.6963 69.0847 84.993L70.5175 86.0085L71.6516 72.127C77.8685 72.3954 82.1156 70.2713 84.8712 67.3545C87.7335 64.3246 88.9125 60.5217 89.1258 57.9118L90.6725 38.9788C91.2898 31.4228 88.8995 26.3501 85.5268 23.1195C82.1926 19.9259 77.9843 18.6134 75.0396 18.3728C69.5847 17.9272 43.0354 15.024 30.7846 14.7585C24.387 14.6198 20.0082 16.9759 17.1714 20.4992C14.3712 23.9773 13.1474 28.4981 12.8108 32.618L11.2641 51.551Z"
          fill="#0a0a0a"
        />
        <path
          d="M42.7237 27.4322C46.2981 26.6567 50.3311 27.7496 52.3626 30.7871C54.3095 29.7528 56.8839 29.1414 59.3651 29.3883C62.316 29.682 65.2062 31.2067 66.6514 34.7031C67.4064 36.5298 67.3189 38.6738 66.6966 40.831C66.0715 42.998 64.8834 45.2663 63.3096 47.4201C60.1682 51.7191 55.3823 55.7063 50.0906 57.5933L49.6428 57.7524L49.2356 57.5062C46.6716 55.9514 42.9608 53.1596 39.9354 49.6666C36.9294 46.196 34.4653 41.8747 34.716 37.2901C35.0298 31.5573 38.6989 28.3056 42.7237 27.4322ZM51.095 32.5941C49.8371 29.925 46.4621 28.6676 43.1476 29.3867C39.9316 30.0847 36.9734 32.6358 36.7127 37.3989C36.5041 41.2112 38.5661 45.0306 41.4477 48.3575C44.1532 51.4811 47.4632 54.0319 49.8653 55.5436C54.5431 53.7509 58.8379 50.1497 61.6952 46.2395C63.1659 44.2267 64.2291 42.1677 64.7745 40.2767C65.3228 38.3762 65.3248 36.7314 64.8024 35.4674C63.6923 32.7817 61.5334 31.6142 59.1666 31.3787C56.7471 31.138 54.187 31.8908 52.5609 32.9949L51.593 33.6527L51.095 32.5941Z"
          fill="#0a0a0a"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M52.7243 30.5104C50.8207 27.6786 47.5252 26.5596 44.4948 27.3953C43.5513 27.6555 42.6529 28.0973 41.8394 28.7164C45.7294 27.3719 50.3294 28.6257 51.999 32.168C55.6288 29.7031 63.1715 28.9029 65.7268 35.085C68.2818 41.2665 59.8965 53.0343 49.7544 56.6509C47.9561 55.5604 45.5701 53.8385 43.2754 51.6887C44.5253 53.3997 45.8607 54.8942 47.1184 56.0095C47.8319 56.6422 48.5394 57.1698 49.209 57.5445C49.8605 57.9091 50.5653 58.1798 51.2501 58.1798C51.9322 58.1798 52.7476 57.8777 53.5422 57.4874C54.3776 57.0772 55.3365 56.4941 56.3519 55.7851C58.3842 54.366 60.7077 52.397 62.8114 50.1693C64.9099 47.9473 66.8295 45.4261 68.0097 42.8973C69.183 40.3829 69.6936 37.7059 68.6992 35.3001C66.0756 28.9526 57.9719 26.5012 52.7243 30.5104Z"
          fill="#0a0a0a"
        />
      </g>
      <defs>
        <clipPath id="pinmerge-comment-icon-clip">
          <rect width="100" height="100" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}
